import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { mangaMap } from '@/mocks/manga';
import { getFeaturedManga } from '@/mocks/manga';
import MangaCard from '@/components/feature/MangaCard';

export default function MangaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const manga = id ? mangaMap[id] : undefined;
  const relatedManga = getFeaturedManga().filter((m) => m.id !== id).slice(0, 4);

  useEffect(() => {
    if (manga) {
      document.title = `${manga.title} - 下拉式完整版免费阅读 - 亲亲漫画网`;
    }
  }, [manga]);

  if (!manga) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 text-center py-20">
          <p className="text-foreground-500">漫画不存在</p>
          <Link to="/" className="text-primary-600 hover:underline mt-4 inline-block text-sm">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const canonicalUrl = `https://yutuocq.cn/manga/${manga.id}`;

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${manga.title} - 亲亲漫画网下拉式完整版免费阅读`,
            description: manga.description,
            author: { '@type': 'Person', name: manga.author },
            datePublished: manga.chapters[0]?.updateTime,
            dateModified: manga.updateTime,
            url: canonicalUrl,
            image: manga.cover,
            publisher: {
              '@type': 'Organization',
              name: '亲亲漫画网',
            },
          }),
        }}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: manga.title },
          ]}
        />

        <article className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-shrink-0 w-full sm:w-64">
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={manga.cover}
                  alt={`${manga.title} - 亲亲漫画网下拉式完整版免费阅读`}
                  title={`${manga.title} - 下拉式完整版免费阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                {manga.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-accent-500 text-sm"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-800">{manga.rating}</span>
                </div>
                <span className="text-foreground-300">|</span>
                <span className="text-sm text-foreground-600">{manga.author}</span>
                <span className="text-foreground-300">|</span>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  manga.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {manga.status}
                </span>
                <span className="text-foreground-300">|</span>
                <span className="text-sm text-foreground-500">{manga.views.toLocaleString()} 次阅读</span>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                {manga.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-secondary-50 text-secondary-700 border border-secondary-200/50"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-lg p-4 mb-6">
                <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{manga.description}</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  className="flex items-center justify-center gap-2 px-8 py-3 text-sm font-semibold rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-play-circle-line"></i>
                  </div>
                  开始阅读（第1话）
                </button>
                <button
                  className="flex items-center justify-center gap-2 px-8 py-3 text-sm font-semibold rounded-full bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/60 transition-all whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-bookmark-line"></i>
                  </div>
                  收藏漫画
                </button>
              </div>
            </div>
          </div>

          <section className="mt-10">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold font-heading text-foreground-950">
                章节列表
                <span className="text-sm font-normal text-foreground-500 ml-2">共 {manga.totalChapters} 话</span>
              </h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 text-xs font-medium rounded-full bg-background-100 text-foreground-600 border border-background-200/60 whitespace-nowrap">
                  正序
                </button>
                <button className="px-3 py-1.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700 border border-primary-200/60 whitespace-nowrap">
                  倒序
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {manga.chapters.slice().reverse().map((chapter) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 border border-background-200/60 hover:border-primary-200 hover:bg-primary-50/30 transition-all cursor-pointer group"
                >
                  <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors">
                    {chapter.title || `第${chapter.number}话`}
                  </span>
                  <span className="text-xs text-foreground-400">{chapter.updateTime}</span>
                </div>
              ))}
            </div>
          </section>

          {relatedManga.length > 0 && (
            <section className="mt-14">
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-5">
                猜你喜欢
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-product-shop="">
                {relatedManga.map((m) => (
                  <MangaCard key={m.id} manga={m} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </div>
  );
}